import { createStore } from 'zustand/vanilla'
import { DEMO_SCENARIO_ID, DEMO_SCENARIO_NAME } from '@/config/demoConfig'

/**
 * Application session: current scenario, workspace, tier, the loading
 * registry, the snackbar. Ported verbatim from the Pinia setup store (Phase 1B);
 * 90 files read it through the Pinia shell, React reads it through useAppState.
 */
export type ActiveLoad = {
  id: number
  message: string
  estimatedSeconds: number | null
  startTime: number
  expiresAt: number
  legacy: boolean
}
export type LoadingTiming = {
  estimatedSeconds: number | null
  startTime: number | null
  elapsedSeconds: number
  remainingSeconds: number | null
}
export type Snackbar = { show: boolean; message: string; color: string; timeout: number }

export type AppState = {
  currentScenario: string
  currentScenarioId: number
  scenarios: string[]
  baselineScenarioId: number
  optimizedScenarioId: number
  subscriptionTier: string
  /**
   * The map view has initialised its scenarios, so the stat bar may show.
   * It was a MapView-local ref with one writer, which a React bar cannot read.
   */
  scenariosReady: boolean
  workspaceId: number
  /** The loading registry; internal to the store in Pinia, carried in state here. */
  activeLoads: ActiveLoad[]
  isLoading: boolean
  loadingMessage: string
  loadingTiming: LoadingTiming
  snackbar: Snackbar
  isOptimized: boolean
  hasRunOptimizationThisSession: boolean
  currentResultType: 'optimization' | 'simulation'
  globalDisplayUnit: string | null
}

const DEFAULT_LOAD_TTL_MS = 10 * 60 * 1000
let nextLoadId = 0

const idleTiming = (): LoadingTiming => ({ estimatedSeconds: null, startTime: null, elapsedSeconds: 0, remainingSeconds: null })

function initialState(): AppState {
  return {
    currentScenario: DEMO_SCENARIO_NAME as string,
    currentScenarioId: DEMO_SCENARIO_ID as number,
    scenarios: [DEMO_SCENARIO_NAME as string],
    baselineScenarioId: DEMO_SCENARIO_ID as number,
    optimizedScenarioId: 2,
    subscriptionTier: 'demo',
    scenariosReady: false,
    workspaceId: 0,
    activeLoads: [],
    isLoading: false,
    loadingMessage: 'Loading...',
    loadingTiming: idleTiming(),
    snackbar: { show: false, message: '', color: 'success', timeout: 3000 },
    isOptimized: false,
    hasRunOptimizationThisSession: false,
    currentResultType: 'optimization',
    globalDisplayUnit: null,
  }
}

export const appStore = createStore<AppState>(() => initialState())

/** isLoading, loadingMessage and loadingTiming follow the newest active load — one setState. */
function withLoads(activeLoads: ActiveLoad[]) {
  const s = appStore.getState()
  const newest = activeLoads[activeLoads.length - 1] || null
  let loadingTiming = s.loadingTiming
  if (!newest) {
    loadingTiming = idleTiming()
  } else if (s.loadingTiming.startTime !== newest.startTime || s.loadingTiming.estimatedSeconds !== newest.estimatedSeconds) {
    loadingTiming = {
      estimatedSeconds: newest.estimatedSeconds,
      startTime: newest.estimatedSeconds ? newest.startTime : null,
      elapsedSeconds: 0,
      remainingSeconds: newest.estimatedSeconds,
    }
  }
  appStore.setState({ activeLoads, isLoading: !!newest, loadingMessage: newest ? newest.message : 'Loading...', loadingTiming })
}

export const appSelectors = {
  isDemoExperience: (s: AppState) => s.workspaceId === 0 || s.subscriptionTier === 'demo',
  canUploadData: (s: AppState) => !(s.workspaceId === 0 || s.subscriptionTier === 'demo'),
}

export const appActions = {
  /** Register an operation; returns a handle for endLoading(). */
  beginLoading(message = 'Loading...', estimatedSeconds: number | null = null, ttlMs = DEFAULT_LOAD_TTL_MS): number {
    const id = ++nextLoadId
    const startTime = Date.now()
    withLoads([...appStore.getState().activeLoads, { id, message, estimatedSeconds, startTime, expiresAt: startTime + ttlMs, legacy: false }])
    return id
  },
  /** Release a handle from beginLoading(). Safe to call twice, or with null. */
  endLoading(id: number | null | undefined) {
    if (id == null) return
    const loads = appStore.getState().activeLoads
    const remaining = loads.filter((load) => load.id !== id)
    if (remaining.length !== loads.length) withLoads(remaining)
  },
  /**
   * Legacy boolean API, retained for the ~30 call sites that do not overlap.
   * Among setLoading() users the last call still wins; it owns a single
   * reserved slot in the registry, so a setLoading(false) cannot yank the
   * spinner away from an operation holding a beginLoading() handle.
   */
  setLoading(loading: boolean, message = 'Loading...', estimatedSeconds: number | null = null) {
    const withoutLegacy = appStore.getState().activeLoads.filter((load) => !load.legacy)
    if (loading) {
      const startTime = Date.now()
      withLoads([...withoutLegacy, { id: ++nextLoadId, message, estimatedSeconds, startTime, expiresAt: startTime + DEFAULT_LOAD_TTL_MS, legacy: true }])
    } else {
      withLoads(withoutLegacy)
    }
  },
  /** Escape hatch: drop every outstanding load. For error recovery only. */
  resetLoading() {
    withLoads([])
  },
  updateLoadingTiming() {
    const now = Date.now()
    const s = appStore.getState()
    const live = s.activeLoads.filter((load) => load.expiresAt > now)
    if (live.length !== s.activeLoads.length) {
      const dropped = s.activeLoads.filter((load) => load.expiresAt <= now)
      console.warn('[appStore] Dropping loading state that was never released:', dropped.map((load) => load.message))
      withLoads(live)
    }
    const t = appStore.getState().loadingTiming
    if (t.startTime && t.estimatedSeconds) {
      const elapsed = Math.floor((Date.now() - t.startTime) / 1000)
      appStore.setState({ loadingTiming: { ...t, elapsedSeconds: elapsed, remainingSeconds: Math.max(0, t.estimatedSeconds - elapsed) } })
    }
  },
  showSnackbar(message: string, color = 'success', timeout = 3000) {
    appStore.setState({ snackbar: { show: true, message, color, timeout } })
  },
  /** Hide it without clearing the message: the component fades out on `show`. */
  hideSnackbar() {
    const { snackbar } = appStore.getState()
    if (snackbar.show) appStore.setState({ snackbar: { ...snackbar, show: false } })
  },
  setCurrentScenario(scenario: string, scenarioId: number | null = null) {
    let id = appStore.getState().currentScenarioId
    if (scenarioId !== null) {
      id = scenarioId
    } else if (scenario === 'Current Baseline' || scenario === 'Baseline') {
      id = 1
    } else if (scenario === 'Optimized Scenario' || scenario === 'Optimized Baseline' || scenario === 'Optimized Network') {
      id = 2
    }
    appStore.setState({ currentScenario: scenario, currentScenarioId: id, isOptimized: id > 1 })
  },
  addScenario(scenario: string) {
    const s = appStore.getState()
    if (!s.scenarios.includes(scenario)) appStore.setState({ scenarios: [...s.scenarios, scenario] })
  },
  markOptimizationRun() {
    appStore.setState({ isOptimized: true, hasRunOptimizationThisSession: true })
  },
  clearOptimizationState() {
    appStore.setState({ isOptimized: false, hasRunOptimizationThisSession: false })
  },
  /**
   * The scenario the stat bar compares against. It was written straight through
   * the Pinia proxy from MapView's writable computed — one of the direct writes
   * Phase 1B catalogued — which a command outside Vue cannot do.
   */
  setBaselineScenarioId(id: number) {
    const next = Number(id)
    if (Number.isFinite(next) && next !== appStore.getState().baselineScenarioId) {
      appStore.setState({ baselineScenarioId: next })
    }
  },
  setOptimizedScenarioId(id: number) {
    const next = Number(id)
    if (Number.isFinite(next) && next !== appStore.getState().optimizedScenarioId) {
      appStore.setState({ optimizedScenarioId: next })
    }
  },
  setCurrentResultType(resultType: string) {
    if (resultType === 'optimization' || resultType === 'simulation') appStore.setState({ currentResultType: resultType })
  },
  setGlobalDisplayUnit(unit: string | null | undefined) {
    appStore.setState({ globalDisplayUnit: unit || null })
  },
  setScenariosReady: (ready: boolean) => appStore.setState({ scenariosReady: ready }),
  setSubscriptionTier(tier: string | null | undefined) {
    appStore.setState({ subscriptionTier: tier || 'demo' })
  },
  setWorkspaceId(id: number | string | null | undefined) {
    const parsed = typeof id === 'string' ? parseInt(id, 10) : id
    appStore.setState({ workspaceId: Number.isFinite(parsed as number) ? (parsed as number) : 0 })
  },
  /** Reconciles the store with ?workspace_id= in the URL; returns the id to use. */
  validateWorkspaceId(): number {
    const urlParams = new URLSearchParams(window.location.search)
    const workspaceIdFromUrl = urlParams.get('workspace_id')
    const storeWorkspaceId = appStore.getState().workspaceId || 0
    let effectiveWorkspaceId = storeWorkspaceId
    if (workspaceIdFromUrl) {
      const parsedUrlId = parseInt(workspaceIdFromUrl, 10)
      if (Number.isFinite(parsedUrlId)) {
        effectiveWorkspaceId = parsedUrlId
        if (parsedUrlId !== storeWorkspaceId) appStore.setState({ workspaceId: parsedUrlId })
      }
    }
    return effectiveWorkspaceId
  },
}

export function __resetAppForTests() {
  appStore.setState(initialState(), true)
}
